// src/pages/finance-portal/revenue-recognition/RevenueRecognition.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment, Autocomplete, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, LinearProgress, Divider,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalculateIcon from '@mui/icons-material/Calculate';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    COMPLETED: { bg: '#dbeafe', color: '#1e40af' },
    CANCELLED: { bg: '#f3f4f6', color: '#6b7280' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Cancelled', value: 'CANCELLED' },
];

const RECOGNITION_METHODS = [
    { value: 'STRAIGHT_LINE', label: 'Straight Line' },
    { value: 'MILESTONE', label: 'Milestone' },
    { value: 'USAGE_BASED', label: 'Usage Based' },
    { value: 'PERCENTAGE_COMPLETION', label: '% Completion' },
    { value: 'POINT_IN_TIME', label: 'Point in Time' },
];

const ENTRY_STATUS_COLORS = {
    PENDING: { bg: '#fef9c3', color: '#854d0e' },
    POSTED: { bg: '#dcfce7', color: '#166534' },
    CANCELLED: { bg: '#f3f4f6', color: '#6b7280' },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getMethodLabel(method) {
    const found = RECOGNITION_METHODS.find(m => m.value === method);
    return found ? found.label : (method || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getProgressColor(pct) {
    if (pct >= 75) return '#22c55e';
    if (pct >= 25) return '#eab308';
    return '#ef4444';
}

export default function RevenueRecognition() {
    const navigate = useNavigate();
    const [schedules, setSchedules] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    // Create Schedule dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [invoiceOptions, setInvoiceOptions] = useState([]);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const invoiceTimer = useRef(null);
    const [createMethod, setCreateMethod] = useState('STRAIGHT_LINE');
    const [createTotalAmount, setCreateTotalAmount] = useState('');
    const [createStartDate, setCreateStartDate] = useState('');
    const [createEndDate, setCreateEndDate] = useState('');
    const [milestones, setMilestones] = useState([{ name: '', percentage: '' }]);

    // Detail dialog
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailSchedule, setDetailSchedule] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Action states
    const [calculating, setCalculating] = useState(null);
    const [posting, setPosting] = useState(null);

    // Report
    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);

    const fetchSchedules = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/revenue-schedules', { params });
            if (res.data.success) {
                setSchedules(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load revenue schedules');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, search]);

    useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

    // Fetch report
    useEffect(() => {
        setReportLoading(true);
        axiosInstance.get('/api/finance/revenue-recognition/report')
            .then(res => {
                if (res.data.success) {
                    setReport(res.data.data);
                }
            })
            .catch(() => {})
            .finally(() => setReportLoading(false));
    }, []);

    // Invoice search for create dialog
    useEffect(() => {
        if (invoiceTimer.current) clearTimeout(invoiceTimer.current);
        if (!invoiceSearch || invoiceSearch.length < 2) {
            setInvoiceOptions([]);
            return;
        }
        setInvoiceLoading(true);
        invoiceTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/finance/invoices', { params: { search: invoiceSearch, per_page: 20 } });
                if (res.data.success) {
                    setInvoiceOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search invoices', err);
            } finally {
                setInvoiceLoading(false);
            }
        }, 300);
        return () => { if (invoiceTimer.current) clearTimeout(invoiceTimer.current); };
    }, [invoiceSearch]);

    // Auto-fill total amount from selected invoice
    useEffect(() => {
        if (selectedInvoice && selectedInvoice.total != null) {
            setCreateTotalAmount(String(selectedInvoice.total));
        }
    }, [selectedInvoice]);

    const handleCreate = async () => {
        if (!selectedInvoice || !createMethod || !createTotalAmount) return;
        setCreating(true);
        setError(null);
        try {
            const payload = {
                invoice_id: selectedInvoice.id,
                recognition_method: createMethod,
                total_amount: parseFloat(createTotalAmount) || 0,
                start_date: createStartDate,
                end_date: createEndDate,
            };
            if (createMethod === 'MILESTONE') {
                payload.milestones = milestones
                    .filter(m => m.name && m.percentage)
                    .map(m => ({ name: m.name, percentage: parseFloat(m.percentage) || 0 }));
            }
            await axiosInstance.post('/api/finance/revenue-schedules', payload);
            setCreateOpen(false);
            resetCreateForm();
            fetchSchedules();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create revenue schedule');
        } finally {
            setCreating(false);
        }
    };

    const resetCreateForm = () => {
        setSelectedInvoice(null);
        setCreateMethod('STRAIGHT_LINE');
        setCreateTotalAmount('');
        setCreateStartDate('');
        setCreateEndDate('');
        setMilestones([{ name: '', percentage: '' }]);
    };

    const handleCalculate = async (scheduleId) => {
        setCalculating(scheduleId);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/revenue-schedules/${scheduleId}/calculate`);
            fetchSchedules();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to calculate entries');
        } finally {
            setCalculating(null);
        }
    };

    const handlePostEntries = async (scheduleId) => {
        setPosting(scheduleId);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/revenue-schedules/${scheduleId}/post-entries`);
            fetchSchedules();
            if (detailSchedule && detailSchedule.id === scheduleId) {
                loadDetail(scheduleId);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to post entries');
        } finally {
            setPosting(null);
        }
    };

    const loadDetail = async (scheduleId) => {
        setDetailLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/revenue-schedules/${scheduleId}`);
            if (res.data.success) {
                setDetailSchedule(res.data.data);
                setDetailOpen(true);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load schedule details');
        } finally {
            setDetailLoading(false);
        }
    };

    const addMilestone = () => {
        setMilestones([...milestones, { name: '', percentage: '' }]);
    };

    const updateMilestone = (index, field, value) => {
        const updated = [...milestones];
        updated[index] = { ...updated[index], [field]: value };
        setMilestones(updated);
    };

    const removeMilestone = (index) => {
        if (milestones.length > 1) {
            setMilestones(milestones.filter((_, i) => i !== index));
        }
    };

    const getStatusLabel = (status) => {
        if (!status) return '';
        return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const tabIndex = STATUS_TABS.findIndex(t => t.value === statusFilter);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TrendingUpIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Revenue Recognition</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Track and manage revenue recognition schedules</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Create Schedule
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Report Summary */}
                {report && !reportLoading && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', fontWeight: 600, mb: 0.5 }}>Total Revenue</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>{formatCurrency(report.total_revenue)}</Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', fontWeight: 600, mb: 0.5 }}>Recognized</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: ACCENT }}>{formatCurrency(report.total_recognized)}</Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', fontWeight: 600, mb: 0.5 }}>Deferred</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#f59e0b' }}>{formatCurrency(report.total_deferred)}</Typography>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* Report by Period */}
                {report && report.periods && report.periods.length > 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Revenue by Period</Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                {report.periods.map((period, idx) => {
                                    const totalForPeriod = (parseFloat(period.recognized) || 0) + (parseFloat(period.deferred) || 0);
                                    const recognizedPct = totalForPeriod > 0 ? ((parseFloat(period.recognized) || 0) / totalForPeriod) * 100 : 0;
                                    return (
                                        <Box key={idx} sx={{
                                            flex: '1 1 140px', maxWidth: 200,
                                            p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#fafafa',
                                        }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: '#64748b', mb: 1 }}>{period.period}</Typography>
                                            <Box sx={{ mb: 0.5 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={recognizedPct}
                                                    sx={{
                                                        height: 6, borderRadius: 3,
                                                        bgcolor: '#fef3c7',
                                                        '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 },
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: ACCENT, fontWeight: 600 }}>{formatCurrency(period.recognized)}</Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#f59e0b', fontWeight: 600 }}>{formatCurrency(period.deferred)}</Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Status Tabs */}
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, idx) => { setStatusFilter(STATUS_TABS[idx].value); setPage(0); }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, textTransform: 'none', fontWeight: 600,
                            fontSize: '0.85rem', color: '#64748b', minHeight: 40,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    {STATUS_TABS.map(t => <Tab key={t.value} label={t.label} />)}
                </Tabs>

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search schedules..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Invoice #', 'Method', 'Total Amount', 'Recognized', 'Deferred', 'Progress', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : schedules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <TrendingUpIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No revenue schedules found</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => setCreateOpen(true)}
                                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                                            >
                                                Create your first schedule
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : schedules.map(sched => {
                                    const statusStyle = STATUS_COLORS[sched.status] || {};
                                    const totalAmt = parseFloat(sched.total_amount) || 0;
                                    const recognized = parseFloat(sched.recognized_amount) || 0;
                                    const deferred = parseFloat(sched.deferred_amount) || 0;
                                    const pct = totalAmt > 0 ? (recognized / totalAmt) * 100 : 0;
                                    const progressColor = getProgressColor(pct);
                                    return (
                                        <TableRow
                                            key={sched.id}
                                            hover
                                            sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {sched.invoice_number || '\u2014'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {getMethodLabel(sched.recognition_method)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(totalAmt)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: ACCENT }}>
                                                {formatCurrency(recognized)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#f59e0b' }}>
                                                {formatCurrency(deferred)}
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 140 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(pct, 100)}
                                                        sx={{
                                                            flex: 1, height: 8, borderRadius: 4,
                                                            bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 4 },
                                                        }}
                                                    />
                                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: progressColor, minWidth: 35 }}>
                                                        {Math.round(pct)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(sched.status)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View Details">
                                                        <IconButton size="small" onClick={() => loadDetail(sched.id)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {sched.status === 'ACTIVE' && (
                                                        <>
                                                            <Tooltip title="Calculate Entries">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleCalculate(sched.id)}
                                                                    disabled={calculating === sched.id}
                                                                    sx={{ color: '#94a3b8', '&:hover': { color: '#8b5cf6' } }}
                                                                >
                                                                    {calculating === sched.id ? <CircularProgress size={16} /> : <CalculateIcon fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Post Entries">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handlePostEntries(sched.id)}
                                                                    disabled={posting === sched.id}
                                                                    sx={{ color: '#94a3b8', '&:hover': { color: ACCENT } }}
                                                                >
                                                                    {posting === sched.id ? <CircularProgress size={16} /> : <PublishIcon fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Create Schedule Dialog */}
            <Dialog open={createOpen} onClose={() => { setCreateOpen(false); resetCreateForm(); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Create Revenue Schedule</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <Autocomplete
                            size="small"
                            options={invoiceOptions}
                            getOptionLabel={opt => opt ? `${opt.invoice_number || 'INV'} - ${formatCurrency(opt.total)}` : ''}
                            value={selectedInvoice}
                            onChange={(_, val) => setSelectedInvoice(val)}
                            onInputChange={(_, val) => setInvoiceSearch(val)}
                            loading={invoiceLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Invoice"
                                    required
                                    InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px' },
                                        endAdornment: (
                                            <>
                                                {invoiceLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="Type to search invoices"
                        />
                        <FormControl size="small">
                            <InputLabel sx={{ fontFamily }}>Recognition Method</InputLabel>
                            <Select
                                value={createMethod}
                                onChange={e => setCreateMethod(e.target.value)}
                                label="Recognition Method"
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {RECOGNITION_METHODS.map(m => (
                                    <MenuItem key={m.value} value={m.value} sx={{ fontFamily }}>{m.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small" label="Total Amount" type="number" required
                            value={createTotalAmount}
                            onChange={e => setCreateTotalAmount(e.target.value)}
                            inputProps={{ min: 0, step: '0.01' }}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                size="small" type="date" label="Start Date" required
                                value={createStartDate}
                                onChange={e => setCreateStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" type="date" label="End Date" required
                                value={createEndDate}
                                onChange={e => setCreateEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </Box>

                        {/* Milestones (for MILESTONE method) */}
                        {createMethod === 'MILESTONE' && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Milestones</Typography>
                                    <Button size="small" startIcon={<AddIcon />} onClick={addMilestone} sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}>
                                        Add Milestone
                                    </Button>
                                </Box>
                                {milestones.map((ms, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center' }}>
                                        <TextField
                                            size="small"
                                            placeholder="Milestone name"
                                            value={ms.name}
                                            onChange={e => updateMilestone(idx, 'name', e.target.value)}
                                            InputProps={{ sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' } }}
                                            sx={{ flex: 2 }}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            placeholder="%"
                                            value={ms.percentage}
                                            onChange={e => updateMilestone(idx, 'percentage', e.target.value)}
                                            inputProps={{ min: 0, max: 100, step: '0.01' }}
                                            InputProps={{ sx: { fontFamily, borderRadius: '8px', fontSize: '0.85rem' } }}
                                            sx={{ flex: 1 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => removeMilestone(idx)}
                                            disabled={milestones.length <= 1}
                                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mt: 0.5 }}>
                                    Total: {milestones.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0).toFixed(1)}% (should equal 100%)
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => { setCreateOpen(false); resetCreateForm(); }} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={creating || !selectedInvoice || !createTotalAmount || !createStartDate || !createEndDate}
                        startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {creating ? 'Creating...' : 'Create Schedule'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Schedule Details {detailSchedule?.invoice_number ? `- ${detailSchedule.invoice_number}` : ''}
                    <IconButton onClick={() => setDetailOpen(false)} size="small" sx={{ color: '#94a3b8' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {detailLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: ACCENT }} />
                        </Box>
                    ) : detailSchedule ? (
                        <Box>
                            {/* Summary row */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Method</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{getMethodLabel(detailSchedule.recognition_method)}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Total</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(detailSchedule.total_amount)}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Recognized</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(detailSchedule.recognized_amount)}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Deferred</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 700, color: '#f59e0b' }}>{formatCurrency(detailSchedule.deferred_amount)}</Typography>
                                </Box>
                            </Box>

                            {/* Entries table */}
                            {detailSchedule.entries && detailSchedule.entries.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                {['Recognition Date', 'Amount', 'Status', 'Journal Entry'].map(h => (
                                                    <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {detailSchedule.entries.map((entry, idx) => {
                                                const entryStatusStyle = ENTRY_STATUS_COLORS[entry.status] || {};
                                                return (
                                                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                            {formatDate(entry.recognition_date)}
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                            {formatCurrency(entry.amount)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={getStatusLabel(entry.status)}
                                                                size="small"
                                                                sx={{
                                                                    fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                                    bgcolor: entryStatusStyle.bg, color: entryStatusStyle.color,
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                            {entry.journal_entry_id ? (
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => { setDetailOpen(false); navigate(`/finance-portal/journal-entries/${entry.journal_entry_id}`); }}
                                                                    sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: '#3b82f6' }}
                                                                >
                                                                    JE #{entry.journal_entry_number || entry.journal_entry_id}
                                                                </Button>
                                                            ) : (
                                                                '\u2014'
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No entries yet. Calculate entries to generate the recognition schedule.</Typography>
                                </Box>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {detailSchedule && detailSchedule.status === 'ACTIVE' && (
                        <>
                            <Button
                                variant="outlined"
                                onClick={() => handleCalculate(detailSchedule.id)}
                                disabled={calculating === detailSchedule.id}
                                startIcon={calculating === detailSchedule.id ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', borderColor: '#8b5cf6', color: '#8b5cf6' }}
                            >
                                Calculate
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => handlePostEntries(detailSchedule.id)}
                                disabled={posting === detailSchedule.id}
                                startIcon={posting === detailSchedule.id ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                Post Entries
                            </Button>
                        </>
                    )}
                    <Button onClick={() => setDetailOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
